import { useState } from "react";
import type { UploadResponse } from "../api";
import { addFingerprint } from "../api";
import { FriendlyTypeConfirm } from "./FriendlyTypeConfirm";
import { InputOutputView } from "./InputOutputView";

type Props = {
  result: UploadResponse;
  filename?: string;
  onTypeCreated: () => void;
  onRetryOcr?: () => void;
};

export function VerdictPanel({ result, filename, onTypeCreated, onRetryOcr }: Props) {
  // Match-flow: toon IN/UIT view
  if (result.verdict === "match") {
    return <InputOutputView result={result} filename={filename} onRetryOcr={onRetryOcr} />;
  }

  // Twijfel-flow: top-matches banner + volledige preview-view (backend doet extractie
  // tegen beste kandidaat zodat user visueel kan bevestigen of het echt het goede type is).
  if (result.verdict === "twijfel") {
    return (
      <TwijfelFlow result={result} filename={filename} onTypeCreated={onTypeCreated} onRetryOcr={onRetryOcr} />
    );
  }

  // Nieuw-flow: toon IN view + klant-vriendelijke confirm-UI.
  // (Technische TemplateEditor zit in FriendlyTypeConfirm onder "Geavanceerd".)
  return (
    <div className="space-y-4">
      <InputOutputView result={result} filename={filename} onRetryOcr={onRetryOcr} />
      {result.suggested_template && (
        <FriendlyTypeConfirm
          fingerprint={result.fingerprint}
          suggestion={result.suggested_template}
          extracted={result.extracted}
          onCreated={onTypeCreated}
        />
      )}
    </div>
  );
}

function TwijfelFlow({
  result,
  filename,
  onTypeCreated,
  onRetryOcr,
}: {
  result: UploadResponse;
  filename?: string;
  onTypeCreated: () => void;
  onRetryOcr?: () => void;
}) {
  const [adding, setAdding] = useState<number | null>(null);
  const [added, setAdded] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async (typeId: number) => {
    setAdding(typeId);
    setError(null);
    try {
      await addFingerprint(typeId, result.fingerprint);
      setAdded(typeId);
      // Expliciet NIET onTypeCreated — die reset ook `result` zodat de hele panel
      // weg zou klappen. Bij fingerprint-toevoegen willen we juist de success-
      // bevestiging IN het panel laten staan.
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="font-medium text-amber-900">Mogelijk een bestaand type</div>
        <p className="text-sm text-amber-800 mt-1">
          Lijkt op een bekend type maar niet zeker. Klik een kandidaat om de fingerprint
          van dit document toe te voegen — volgende keer matcht een vergelijkbare upload direct.
        </p>
        <ul className="mt-3 space-y-1">
          {result.matches.slice(0, 3).map((m) => {
            const isAdding = adding === m.type_id;
            const isAdded = added === m.type_id;
            return (
              <li key={m.type_id}>
                <button
                  type="button"
                  onClick={() => !isAdded && !isAdding && handleConfirm(m.type_id)}
                  disabled={isAdding || isAdded}
                  className={`w-full text-left text-sm bg-white border rounded px-3 py-2 flex items-center justify-between gap-3 transition-colors ${
                    isAdded
                      ? "border-green-400 bg-green-50"
                      : "hover:border-amber-400 hover:bg-amber-50 cursor-pointer"
                  } ${isAdding ? "opacity-60" : ""}`}
                >
                  <span className="flex-1 min-w-0">
                    <strong>{m.naam}</strong>
                    {m.afzender && <span className="text-gray-500 ml-2">— {m.afzender}</span>}
                  </span>
                  <span className="font-mono text-xs text-gray-600">{m.score.toFixed(3)}</span>
                  {isAdding && <span className="text-xs text-amber-700">toevoegen…</span>}
                  {isAdded && <span className="text-xs text-green-700 font-medium">✓ toegevoegd</span>}
                </button>
              </li>
            );
          })}
        </ul>
        {error && (
          <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            {error}
          </div>
        )}
      </div>
      <InputOutputView result={result} filename={filename} onRetryOcr={onRetryOcr} />
      {result.suggested_template && (
        <details className="bg-white border rounded">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
            Of leg aan als nieuw type …
          </summary>
          <div className="px-4 pb-4">
            <FriendlyTypeConfirm
              fingerprint={result.fingerprint}
              suggestion={result.suggested_template}
              extracted={result.extracted}
              onCreated={onTypeCreated}
            />
          </div>
        </details>
      )}
    </div>
  );
}

